/**
 * 전사 형식 감지 (TRANSCRIPT_PROTOCOL §4의 최소 부분). 파싱·정렬·화자 판정은 M4.
 * M2에서는 붙여넣은 텍스트가 무엇인지 알려주고, 두 기기 전사를 한 번에 붙여넣은 경우 나눈다.
 * 원문(RAW)은 절대 바꾸지 않는다 — 나눌 때도 원래 줄을 그대로 쓴다.
 */
const TS = String.raw`(?:\d{1,2}:)?\d{1,2}:\d{2}`;
const GALAXY_HEADER = new RegExp(String.raw`^\s*발화자\s*\d+\s*\(\s*(${TS})\s*\)\s*$`);
const IPHONE_HEADER = new RegExp(String.raw`^\s*(${TS})\s+참석자\s*\d+\s*$`);
const SECTION_GALAXY = /^\s*(galaxy|갤럭시|삼성|samsung)\s*:?\s*$/i;
const SECTION_IPHONE = /^\s*(iphone|아이폰|apple|애플)\s*:?\s*$/i;

export type TranscriptKind = 'galaxy' | 'iphone' | 'combined' | 'unknown' | 'empty';

export type Detection = {
  kind: TranscriptKind;
  galaxyTurns: number;
  iphoneBlocks: number;
  /** 마지막 타임스탬프 (ms). 회기 길이 추정용 */
  lastTimestampMs: number | null;
};

export function parseTimestampMs(ts: string): number {
  const parts = ts.split(':').map(Number);
  const [h, m, s] = parts.length === 3 ? parts : [0, parts[0] ?? 0, parts[1] ?? 0];
  return ((h ?? 0) * 3600 + (m ?? 0) * 60 + (s ?? 0)) * 1000;
}

export function detectTranscript(text: string): Detection {
  if (!text.trim()) return { kind: 'empty', galaxyTurns: 0, iphoneBlocks: 0, lastTimestampMs: null };
  let galaxyTurns = 0;
  let iphoneBlocks = 0;
  let last: number | null = null;
  for (const line of text.split(/\r?\n/)) {
    const g = GALAXY_HEADER.exec(line);
    const i = g ? null : IPHONE_HEADER.exec(line);
    if (g) galaxyTurns++;
    if (i) iphoneBlocks++;
    const ts = g?.[1] ?? i?.[1];
    if (ts) last = Math.max(last ?? 0, parseTimestampMs(ts));
  }
  const kind: TranscriptKind =
    galaxyTurns > 0 && iphoneBlocks > 0
      ? 'combined'
      : galaxyTurns > 0
        ? 'galaxy'
        : iphoneBlocks > 0
          ? 'iphone'
          : 'unknown';
  return { kind, galaxyTurns, iphoneBlocks, lastTimestampMs: last };
}

/**
 * 두 기기 전사가 한 텍스트에 섞여 있으면 기기별로 나눈다.
 * 구분 기준: "Galaxy:" / "iPhone:" 같은 머리줄. 머리줄이 없으면 각 헤더 줄의 형식으로 구간을 나눈다.
 */
export function splitCombined(text: string): { galaxy: string; iphone: string } | null {
  const lines = text.split(/\r?\n/);
  let mode: 'galaxy' | 'iphone' | null = null;
  const out = { galaxy: [] as string[], iphone: [] as string[] };
  for (const line of lines) {
    if (SECTION_GALAXY.test(line)) {
      mode = 'galaxy';
      continue;
    }
    if (SECTION_IPHONE.test(line)) {
      mode = 'iphone';
      continue;
    }
    if (GALAXY_HEADER.test(line)) mode = 'galaxy';
    else if (IPHONE_HEADER.test(line)) mode = 'iphone';
    if (/^\s*_{3,}\s*$/.test(line)) continue; // 구분선
    if (mode) out[mode].push(line);
  }
  const galaxy = out.galaxy.join('\n').trim();
  const iphone = out.iphone.join('\n').trim();
  if (!galaxy || !iphone) return null;
  return { galaxy: galaxy + '\n', iphone: iphone + '\n' };
}
